import { Card, CardContent, Divider, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useEffect, useState } from "react";
import ActionButton from "../button/actionButton";
import EditFxDialog from "../dialog/editFxDialog";
import BankIcon from "../icons/bankIcon";
import { AdminRole } from "../../utils/role";
import { useDataProvider } from "react-admin";
import { useAuth0 } from "@auth0/auth0-react";
import { updateFees } from "../../utils/data/feesDataProvider";

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.surface.primary,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    content: {
        padding: '16px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    sectionHeader: {
        color: theme.text.subtitle,
        fontFamily: 'DM Sans',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '16px',
        opacity: 0.8,
        marginBottom: 8,
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 8px',
        marginBottom: 4,
    },
    rowLeft: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
    },
    icon: {
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    left: {
        display: 'flex',
        flexDirection: 'column',
    },
    name: {
        color: '#FDFDFD',
        fontFamily: 'DM Sans',
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '18px',
        marginTop: 6,
    },
    sub: {
        color: theme.text.subtitle,
        fontFamily: 'SF Pro Text, -apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
        fontSize: 12,
        lineHeight: '16px',
        opacity: 0.5,
    },
    detailsRow: {
        display: 'flex',
        padding: '0 8px 8px 8px',
        marginTop: -16,
    },
    detailsText: {
        marginLeft: 48,
    },
    valueStrong: {
        color: '#FDFDFD',
        opacity: 1,
        fontWeight: 600,
    },
}));

const SettingsSection = ({ customerId, onUnhideVault, onUpdated }) => {
    const classes = useStyles();
    const dataProvider = useDataProvider();
    const { user } = useAuth0();
    const [cardFee, setCardFee] = useState(null);
    const [onRampBankWalletFee, setOnRampBankWalletFee] = useState(null);
    const [onRampCardFee, setOnRampCardFee] = useState(null);
    const [offRampBankWalletFee, setOffRampBankWalletFee] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [editOpen, setEditOpen] = useState(false);
    const [editContext, setEditContext] = useState(null);

    useEffect(() => {
        let mounted = true;
        if (!customerId) return;
        setLoading(true);
        dataProvider.getOne('fees', { customerId })
            .then(({ data }) => {
                if (!mounted) return;
                setCardFee(data?.feeCardSpend || null);
                setOnRampBankWalletFee(data?.feeOnRampBankWallet || null);
                setOnRampCardFee(data?.feeOnRampCard || null);
                setOffRampBankWalletFee(data?.feeOffRampBankWallet || null);
                setErrors(data?.errors || {});
            })
            .catch((e) => {
                if (!mounted) return;
                setErrors({ cardSpend: e, onRampBankWallet: e, onRampCard: e, offRampBankWallet: e });
            })
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [customerId]);
    return (
        <Card elevation={0} className={classes.root}>
            <CardContent className={classes.content}>
                <Typography variant="h6" className={classes.sectionHeader}>FX settings</Typography>
                {[{name: 'Card Spend Fee (Non-USD spending)', sub: ''}, {name: 'On-ramp Fee (Bank Transfer, Mobile Wallet)', sub: ''}, {name: 'On-ramp Fee(Card)', sub: ''}, {name: 'Off-ramp Fee (Bank Transfer, Mobile Wallet)', sub: ''}].map((i, idx) => (
                    <React.Fragment key={i.name}>
                        <div className={classes.row}>
                            <div className={classes.rowLeft}>
                                <div className={classes.icon}><BankIcon /></div>
                                <div className={classes.left}>
                                    <Typography className={classes.name}>{i.name}</Typography>
                                    {i.name !== 'Card Spend Fee (Non-USD spending)' ? (
                                        <Typography className={classes.sub}>{i.sub}</Typography>
                                    ) : null}
                                </div>
                            </div>
                            <AdminRole>
                                <ActionButton size="sm" variant="primary" style={{ width: 96, height: 36 }} onClick={() => {
                                    setEditContext(i.name)
                                    setEditOpen(true)
                                }}>Edit</ActionButton>
                            </AdminRole>
                        </div>
                        {i.name === 'Card Spend Fee (Non-USD spending)' && (
                            <div className={classes.detailsRow}>
                                <Typography className={`${classes.sub} ${classes.detailsText}`}>
                                    {loading && 'Loading...'}
                                    {!loading && errors?.cardSpend && 'Failed to load.'}
                                    {!loading && !errors?.cardSpend && cardFee && cardFee.fxSpread != null && (
                                        <span className={classes.valueStrong}>{`${Number(cardFee.fxSpread).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}</span>
                                    )}
                                    {!loading && !errors?.cardSpend && (!cardFee || cardFee.fxSpread == null) && 'No fx spread configured.'}
                                </Typography>
                            </div>
                        )}
                        {i.name === 'On-ramp Fee (Bank Transfer, Mobile Wallet)' && (
                            <div className={classes.detailsRow}>
                                <Typography className={`${classes.sub} ${classes.detailsText}`}>
                                    {loading && 'Loading...'}
                                    {!loading && errors?.onRampBankWallet && 'Failed to load.'}
                                    {!loading && !errors?.onRampBankWallet && onRampBankWalletFee && onRampBankWalletFee.fxSpread != null && (
                                        <span className={classes.valueStrong}>{`${Number(onRampBankWalletFee.fxSpread).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}</span>
                                    )}
                                    {!loading && !errors?.onRampBankWallet && (!onRampBankWalletFee || onRampBankWalletFee.fxSpread == null) && 'No fx spread configured.'}
                                </Typography>
                            </div>
                        )}
                        {i.name === 'On-ramp Fee(Card)' && (
                            <div className={classes.detailsRow}>
                                <Typography className={`${classes.sub} ${classes.detailsText}`}>
                                    {loading && 'Loading...'}
                                    {!loading && errors?.onRampCard && 'Failed to load.'}
                                    {!loading && !errors?.onRampCard && onRampCardFee && onRampCardFee.fxSpread != null && (
                                        <span className={classes.valueStrong}>{`${Number(onRampCardFee.fxSpread).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}</span>
                                    )}
                                    {!loading && !errors?.onRampCard && (!onRampCardFee || onRampCardFee.fxSpread == null) && 'No fx spread configured.'}
                                </Typography>
                            </div>
                        )}
                        {i.name === 'Off-ramp Fee (Bank Transfer, Mobile Wallet)' && (
                            <div className={classes.detailsRow}>
                                <Typography className={`${classes.sub} ${classes.detailsText}`}>
                                    {loading && 'Loading...'}
                                    {!loading && errors?.offRampBankWallet && 'Failed to load.'}
                                    {!loading && !errors?.offRampBankWallet && offRampBankWalletFee && offRampBankWalletFee.fxSpread != null && (
                                        <span className={classes.valueStrong}>{`${Number(offRampBankWalletFee.fxSpread).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}</span>
                                    )}
                                    {!loading && !errors?.offRampBankWallet && (!offRampBankWalletFee || offRampBankWalletFee.fxSpread == null) && 'No fx spread configured.'}
                                </Typography>
                            </div>
                        )}
                        {idx < 3 && <Divider />}
                    </React.Fragment>
                ))}
                <Divider style={{ margin: '8px 0' }} />
                <EditFxDialog
                    open={editOpen}
                    onBack={() => setEditOpen(false)}
                    onClose={() => setEditOpen(false)}
                    initialFxPercent={(() => {
                        switch (editContext) {
                            case 'Card Spend Fee (Non-USD spending)':
                                return cardFee?.fxSpread ?? null
                            case 'On-ramp Fee (Bank Transfer, Mobile Wallet)':
                                return onRampBankWalletFee?.fxSpread ?? null
                            case 'On-ramp Fee(Card)':
                                return onRampCardFee?.fxSpread ?? null
                            case 'Off-ramp Fee (Bank Transfer, Mobile Wallet)':
                                return offRampBankWalletFee?.fxSpread ?? null
                            default:
                                return null
                        }
                    })()}
                    onUpdate={async (value) => {
                        await updateFees({ customerId, context: editContext, fx: value, createdBy: user?.email })
                        // refresh values
                        setLoading(true)
                        try {
                            const { data } = await dataProvider.getOne('fees', { customerId })
                            setCardFee(data?.feeCardSpend || null)
                            setOnRampBankWalletFee(data?.feeOnRampBankWallet || null)
                            setOnRampCardFee(data?.feeOnRampCard || null)
                            setOffRampBankWalletFee(data?.feeOffRampBankWallet || null)
                            setErrors(data?.errors || {})
                        } finally {
                            setLoading(false)
                        }
                        // notify parent to refresh (e.g., notes/activity)
                        onUpdated && onUpdated()
                    }}
                />
                <Typography variant="h6" className={classes.sectionHeader}>Vault settings</Typography>
                <div className={classes.row}>
                    <div className={classes.rowLeft}>
                        <div className={classes.icon}><BankIcon /></div>
                        <div className={classes.left}>
                            <Typography className={classes.name}>Fireblocks Vaults</Typography>
                            {/* Removed hardcoded expiry label */}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <AdminRole>
                            <ActionButton size="sm" variant="primary" style={{ width: 96, height: 36 }} onClick={onUnhideVault}>Unhide</ActionButton>
                        </AdminRole>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default SettingsSection;


